import { useCallback, useEffect, useRef, useState } from "react";
import {
  deleteMutationRequest,
  fetchMutationRequests,
  getCachedMutationRequests,
  hasMutationRequestsCache,
  optimisticallyRemoveMutationRequest,
  performMutationRequestAction,
  restoreMutationRequestsCache,
  setCachedMutationRequests,
} from "../api/mutationRequests.api";
import type {
  EntityId,
  MutationRequestAction,
  MutationRequestCollection,
} from "../types/mutationRequests.types";

const EMPTY_COLLECTION: MutationRequestCollection = { pending: [], history: [] };

export const useMutationRequests = (enabled: boolean) => {
  const [requests, setRequests] = useState<MutationRequestCollection>(() => getCachedMutationRequests() ?? EMPTY_COLLECTION);
  const [loading, setLoading] = useState(() => enabled && !hasMutationRequestsCache());
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const [processingRequestId, setProcessingRequestId] = useState<EntityId | null>(null);
  const inFlightRefreshRef = useRef<Promise<MutationRequestCollection> | null>(null);
  const processingRef = useRef<EntityId | null>(null);
  const mountedRef = useRef(true);

  useEffect(() => {
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const reload = useCallback(async (force = false) => {
    if (!enabled) {
      setLoading(false);
      return EMPTY_COLLECTION;
    }

    if (inFlightRefreshRef.current) return inFlightRefreshRef.current;

    const hasCache = hasMutationRequestsCache() || Boolean(getCachedMutationRequests());
    if (!hasCache) setLoading(true);
    else setRefreshing(true);
    setError(null);

    const request = fetchMutationRequests(force || hasCache)
      .then((data) => {
        if (mountedRef.current) setRequests(data);
        return data;
      })
      .catch((loadError) => {
        if (mountedRef.current) setError(loadError);
        throw loadError;
      })
      .finally(() => {
        inFlightRefreshRef.current = null;
        if (mountedRef.current) {
          setLoading(false);
          setRefreshing(false);
        }
      });

    inFlightRefreshRef.current = request;
    return request;
  }, [enabled]);

  useEffect(() => {
    void reload().catch(() => undefined);
  }, [reload]);

  const performAction = useCallback(async (id: EntityId, action: MutationRequestAction) => {
    if (processingRef.current) return;
    const previous = getCachedMutationRequests();
    processingRef.current = id;
    setProcessingRequestId(id);

    try {
      await performMutationRequestAction(id, action);
      const next = getCachedMutationRequests();
      if (next && mountedRef.current) setRequests(next);
    } catch (actionError) {
      if (previous) {
        setCachedMutationRequests(previous);
        if (mountedRef.current) setRequests(previous);
      }
      throw actionError;
    } finally {
      processingRef.current = null;
      if (mountedRef.current) setProcessingRequestId(null);
    }
  }, []);

  const removeRequest = useCallback(async (id: EntityId) => {
    if (processingRef.current) return;
    processingRef.current = id;
    setProcessingRequestId(id);
    const previous = optimisticallyRemoveMutationRequest(id);
    const next = getCachedMutationRequests();
    if (next) setRequests(next);

    try {
      await deleteMutationRequest(id);
    } catch (deleteError) {
      restoreMutationRequestsCache(previous);
      const restored = getCachedMutationRequests();
      if (restored && mountedRef.current) setRequests(restored);
      throw deleteError;
    } finally {
      processingRef.current = null;
      if (mountedRef.current) setProcessingRequestId(null);
    }
  }, []);

  return {
    requests,
    loading,
    refreshing,
    error,
    processingRequestId,
    setError,
    reload,
    performAction,
    removeRequest,
  };
};
